import React, { useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { XMarkIcon, ArrowUpIcon, ArrowDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useEducation } from '../../contexts/EducationContext';
import { Education, createEducation, updateEducation, deleteEducation, updateEducationOrder } from '../../firebase/educationService';
import { toast } from 'react-hot-toast';

// Validation schema for education form
const educationSchema = Yup.object().shape({
  institution: Yup.string().required('Institution name is required'),
  degree: Yup.string().required('Degree is required'),
  fieldOfStudy: Yup.string(),
  location: Yup.string(),
  startDate: Yup.string()
    .required('Start date is required')
    .matches(/^\d{4}(-\d{2}(-\d{2})?)?$/, 'Date format must be YYYY-MM or YYYY-MM-DD'),
  endDate: Yup.string()
    .matches(/^\d{4}(-\d{2}(-\d{2})?)?$/, 'Date format must be YYYY-MM or YYYY-MM-DD')
    .nullable(),
  grade: Yup.string(),
  descriptions: Yup.array().of(Yup.string()),
  website: Yup.string().url('Must be a valid URL'),
  achievements: Yup.array().of(Yup.string()),
});

// Initial values for education form
const initialValues: Omit<Education, 'id' | 'createdAt' | 'updatedAt'> = {
  institution: '',
  degree: '',
  fieldOfStudy: '',
  location: '',
  startDate: '',
  endDate: '',
  grade: '',
  descriptions: [''],
  institutionLogo: '',
  website: '',
  achievements: [''],
  order: 0,
};

const EducationEditor: React.FC = () => {
  const { education, loading, refreshEducation } = useEducation();
  const [selectedEducation, setSelectedEducation] = useState<Education | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setLogoPreview(base64);
        setFieldValue('institutionLogo', base64);
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Reset form when adding new education
  const handleAddNew = () => {
    setSelectedEducation(null);
    setIsAdding(true);
    setLogoPreview('');
  };

  // Edit existing education
  const handleEdit = (edu: Education) => {
    setSelectedEducation(edu);
    setIsAdding(false);
    setLogoPreview(edu.institutionLogo || '');
  };

  // Cancel editing or adding
  const handleCancel = () => {
    setSelectedEducation(null);
    setIsAdding(false);
    setLogoPreview('');
  };

  // Delete education entry
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this education entry?')) {
      try {
        await deleteEducation(id);
        toast.success('Education entry deleted successfully');
        refreshEducation();
      } catch (error) {
        console.error('Error deleting education:', error);
        toast.error('Failed to delete education entry');
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      const educationData = { ...values };
      
      // Filter out empty descriptions and achievements
      educationData.descriptions = educationData.descriptions.filter((desc: string) => desc.trim() !== '');
      educationData.achievements = educationData.achievements.filter((achievement: string) => achievement.trim() !== '');
      
      if (selectedEducation && selectedEducation.id) {
        await updateEducation(selectedEducation.id, educationData);
        toast.success('Education updated successfully');
      } else {
        await createEducation(educationData);
        toast.success('Education added successfully');
      }
      
      resetForm();
      setSelectedEducation(null);
      setIsAdding(false);
      setLogoPreview('');
      refreshEducation();
    } catch (error) {
      console.error('Error saving education:', error);
      toast.error('Failed to save education entry');
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;
    
    const items = Array.from(education);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update the order property of each education item
    const reorderedEducation = items.map((item, index) => ({
      id: item.id!,
      order: index
    }));
    
    try {
      await updateEducationOrder(reorderedEducation);
      refreshEducation();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Education Editor</h1>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Manage your education and certification entries here. Drag and drop to reorder.
      </p>
      
      {/* Education List */}
      {education.length > 0 && !isAdding && !selectedEducation && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Education Entries</h2>
            <button
              type="button"
              onClick={handleAddNew}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 transition-colors"
            >
              <PlusIcon className="h-5 w-5 inline-block mr-1" />
              Add New
            </button>
          </div>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="education-list">
              {(provided: any) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {education.map((edu, index) => (
                    <Draggable key={edu.id} draggableId={edu.id!} index={index}>
                      {(provided: any) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-gray-50 dark:bg-gray-800 hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-grow">
                              <div className="flex items-start">
                                {edu.institutionLogo && (
                                  <div className="w-16 h-16 flex-shrink-0 mr-4 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center overflow-hidden">
                                    <img 
                                      src={edu.institutionLogo} 
                                      alt={`${edu.institution} logo`} 
                                      className="w-full h-full object-contain p-1"
                                    />
                                  </div>
                                )}
                                <div className="w-full">
                                  <div className="flex flex-wrap justify-between items-start mb-1">
                                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mr-2">{edu.degree}</h3>
                                    <div className="flex space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEdit(edu)}
                                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDelete(edu.id!)}
                                        className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                                      >
                                        <TrashIcon className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-gray-700 dark:text-gray-300 font-medium">{edu.institution}</p>
                                  {edu.fieldOfStudy && (
                                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                                      {edu.fieldOfStudy}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mt-1 mb-2">
                                    <span>{edu.startDate} - {edu.endDate || 'Present'}</span>
                                    {edu.location && <span className="mx-2">•</span>}
                                    {edu.location && <span>{edu.location}</span>}
                                    {edu.grade && <span className="mx-2">•</span>}
                                    {edu.grade && <span>Grade: {edu.grade}</span>}
                                  </div>
                                  
                                  {(edu.descriptions && edu.descriptions.length > 0) || (edu.achievements && edu.achievements.length > 0) ? (
                                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                      {/* Show descriptions if available */}
                                      {edu.descriptions && edu.descriptions.length > 0 && (
                                        <div className="mb-2">
                                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">Descriptions:</p>
                                          <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400">
                                            {edu.descriptions.slice(0, 1).map((desc, i) => (
                                              <li key={i}>{desc}</li>
                                            ))}
                                            {edu.descriptions.length > 1 && (
                                              <li className="text-gray-500 dark:text-gray-500 italic text-xs">+ {edu.descriptions.length - 1} more</li>
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                      
                                      {/* Show achievements if available */}
                                      {edu.achievements && edu.achievements.length > 0 && (
                                        <div>
                                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">Achievements:</p>
                                          <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400">
                                            {edu.achievements.slice(0, 1).map((achievement, i) => (
                                              <li key={i}>{achievement}</li>
                                            ))}
                                            {edu.achievements.length > 1 && (
                                              <li className="text-gray-500 dark:text-gray-500 italic text-xs">+ {edu.achievements.length - 1} more</li>
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
      
      {/* No Education Message */}
      {education.length === 0 && !isAdding && !selectedEducation && (
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No education entries found. Add your first one now!</p>
          <button
            type="button"
            onClick={handleAddNew}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 transition-colors"
          >
            <PlusIcon className="h-5 w-5 inline-block mr-1" />
            Add Education
          </button>
        </div>
      )}
      
      {/* Education Form */}
      {(isAdding || selectedEducation) && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isAdding ? 'Add New Education' : 'Edit Education'}
            </h2>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <Formik
            initialValues={selectedEducation || initialValues}
            validationSchema={educationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => {
              // Logo upload handler with access to Formik's setFieldValue
              const logoUploadHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
                handleLogoChange(e, setFieldValue);
              };
              
              return (
                <Form className="space-y-6">
                  {/* Logo Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Institution Logo</label>
                    <div className="flex items-center space-x-4">
                      <div 
                        className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-700"
                      >
                        {logoPreview ? (
                          <img 
                            src={logoPreview} 
                            alt="Logo preview" 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-sm text-center p-2">No logo</span>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={logoUploadHandler}
                          accept="image/*"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors"
                        >
                          Select Image
                        </button>
                        {logoPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setLogoPreview('');
                              setFieldValue('institutionLogo', '');
                            }}
                            className="px-3 py-1.5 ml-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 text-sm transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Two-column layout for form fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Institution */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="institution">
                        Institution*
                      </label>
                      <Field
                        id="institution"
                        name="institution"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="University or institution name"
                      />
                      <ErrorMessage name="institution" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Degree */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="degree">
                        Degree/Certificate*
                      </label>
                      <Field
                        id="degree"
                        name="degree"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="Degree or certificate name"
                      />
                      <ErrorMessage name="degree" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Field of Study */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="fieldOfStudy">
                        Field of Study
                      </label>
                      <Field
                        id="fieldOfStudy"
                        name="fieldOfStudy"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="Major or specialization"
                      />
                      <ErrorMessage name="fieldOfStudy" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="location">
                        Location
                      </label>
                      <Field
                        id="location"
                        name="location"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="City, Country"
                      />
                      <ErrorMessage name="location" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Website */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="website">
                        Institution Website
                      </label>
                      <Field
                        id="website"
                        name="website"
                        type="url"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="https://example.edu"
                      />
                      <ErrorMessage name="website" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Grade */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="grade">
                        Grade/GPA
                      </label>
                      <Field
                        id="grade"
                        name="grade"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 3.8/4.0, First Class, etc."
                      />
                      <ErrorMessage name="grade" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="startDate">
                        Start Date* (YYYY-MM)
                      </label>
                      <Field
                        id="startDate"
                        name="startDate"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="2018-09"
                      />
                      <ErrorMessage name="startDate" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* End Date */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="endDate">
                        End Date (YYYY-MM, leave empty for 'Present')
                      </label>
                      <Field
                        id="endDate"
                        name="endDate"
                        type="text"
                        className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="2022-06"
                      />
                      <ErrorMessage name="endDate" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                  </div>
                  
                  {/* Descriptions - Dynamic List */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Descriptions (Courses, Research, etc.)
                    </label>
                    <FieldArray name="descriptions">
                      {({ push, remove }) => (
                        <div className="space-y-3">
                          {values.descriptions && values.descriptions.map((_, index) => (
                            <div key={index} className="flex items-start">
                              <Field
                                name={`descriptions.${index}`}
                                as="textarea"
                                rows={2}
                                className="form-textarea flex-grow rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                placeholder="Enter a description or highlight"
                              />
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="ml-2 p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                                disabled={values.descriptions && values.descriptions.length === 1}
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push('')}
                            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors"
                          >
                            <PlusIcon className="h-4 w-4 inline-block mr-1" />
                            Add Description
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                  
                  {/* Achievements - Dynamic List */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Achievements & Awards
                    </label>
                    <FieldArray name="achievements">
                      {({ push, remove }) => (
                        <div className="space-y-3">
                          {values.achievements && values.achievements.map((_, index) => (
                            <div key={index} className="flex items-start">
                              <Field
                                name={`achievements.${index}`}
                                as="textarea"
                                rows={2}
                                className="form-textarea flex-grow rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                placeholder="Enter an achievement or award"
                              />
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="ml-2 p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                                disabled={values.achievements && values.achievements.length === 1}
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push('')}
                            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors"
                          >
                            <PlusIcon className="h-4 w-4 inline-block mr-1" />
                            Add Achievement
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                  
                  {/* Form Buttons */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Saving...' : selectedEducation ? 'Update Education' : 'Add Education'}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default EducationEditor;